// featureConsent.js -- consent to one feature (SlipSync #1415, Bookies #1421) on the consent ledger.
// Single job: answer whether a signed-in user agreed to a feature's current disclosure, and record an agreement
// with one direct insert while the user waits. It does not use the onboarding outbox (plan D3): feature consents
// never change onboarding outcomes. Nothing is retried in the background: a failure goes back to the caller and
// the user taps again. Both calls are bounded by a deadline (a stalled session refresh cannot hang the UI).
import * as Sentry from '../../lib/errorReporting';
import { fetchLatestConsent, buildConsentRow, insertRows } from './accountRecords';

// bookies-v2 (#1742, Nicky 2026-10-26 "Bump both"): the #1731 B2 record text. It records which text was agreed to; it
// is not a sharing gate (bookies_board shares on any accepted bookies row).
export const FEATURE_CONSENT_VERSIONS = Object.freeze({ slipsync: 'slipsync-v1', bookies: 'bookies-v2' });
export const CONSENT_DEADLINE_MS = 10000;
// `${userId}:${kind}` -> the version known to be on the server. Memory only: a relaunch re-reads once.
const confirmed = new Map();
const keyOf = (userId, kind) => `${userId}:${kind}`;
function versionOf(kind) {
  if (!Object.prototype.hasOwnProperty.call(FEATURE_CONSENT_VERSIONS, kind)) throw new Error(`unknown feature consent kind: ${kind}`);
  return FEATURE_CONSENT_VERSIONS[kind];
}
function within(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms); });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
// Recoverable (a retap can succeed): warning. Terminal (a retap cannot): exception (CC-IMPL-037), as the outbox's
// parked path reports a rejected onboarding row.
const warn = (message, tags) => Sentry.captureMessage(message, { level: 'warning', tags: { function: 'featureConsent', ...tags } });
const fail = (message, tags) => Sentry.captureException(new Error(message), { tags: { function: 'featureConsent', ...tags } });

// -> 'granted' | 'missing', or 'outdated' with distinguishOutdated: the newest accepted row is an earlier version.
// The server shares on any accepted row, so an outdated user IS sharing (#1731 B2, Nova P2-1); only a caller that says
// so (BookiesPage) gets 'outdated', every other caller still sees 'missing'. 'outdated' is never cached.
export async function readFeatureConsent(client, userId, kind, { timeoutMs = CONSENT_DEADLINE_MS, distinguishOutdated = false } = {}) {
  const version = versionOf(kind);
  if (confirmed.get(keyOf(userId, kind)) === version) return 'granted';
  const row = await within(fetchLatestConsent(client, userId, kind), timeoutMs, 'consent read');
  if (row && row.version === version) { confirmed.set(keyOf(userId, kind), version); return 'granted'; }
  if (distinguishOutdated && row && typeof row.version === 'string') return 'outdated';
  return 'missing';
}

export async function recordFeatureConsent(client, userId, kind, { now = Date.now(), timeoutMs = CONSENT_DEADLINE_MS } = {}) {
  const version = versionOf(kind);
  // One deadline for the whole call (#1415 PR 1 GPT advisory 1): the session read and the insert share it, so the
  // user gets an answer within timeoutMs end to end.
  const deadline = Date.now() + timeoutMs;
  const left = () => Math.max(0, deadline - Date.now());
  // The attestation time is consent evidence: an invalid one is refused, never stored as the epoch or retried.
  // A finite value outside the Date range is invalid too (GPT advisory 2): toISOString would throw a RangeError.
  if (typeof now !== 'number' || !Number.isFinite(now) || Number.isNaN(new Date(now).getTime())) {
    fail('feature consent: invalid attestation time', { stage: 'build', kind });
    return 'rejected';
  }
  // Built before any await: only the insert itself can be labelled a timeout below.
  const row = buildConsentRow({ userId, kind, version, attestedAt: new Date(now).toISOString() });
  // The insert carries this user's own token (#1438 F2): a sign-out or account switch mid-call sends nothing.
  let accessToken = null;
  try {
    const { data } = await within(client.auth.getSession(), left(), 'session read');
    const s = data && data.session;
    if (s && s.user && s.user.id === userId) accessToken = s.access_token || null;
  } catch {
    warn('feature consent: session read failed', { stage: 'session', kind });
    return 'failed';
  }
  if (!accessToken) return 'failed';
  let res;
  try {
    res = await within(insertRows(client, [row], { accessToken }), left(), 'consent insert');
  } catch {
    // A timed-out insert may still land: the next read finds it and grants.
    warn('feature consent: insert timed out', { stage: 'timeout', kind });
    return 'failed';
  }
  if (res.ok) { confirmed.set(keyOf(userId, kind), version); return 'saved'; }
  if (res.terminal) { fail('feature consent: insert rejected', { stage: 'rejected', kind, code: res.code }); return 'rejected'; }
  warn('feature consent: insert failed', { stage: 'transient', kind });
  return 'failed';
}

export function forgetFeatureConsent(userId, kind) { confirmed.delete(keyOf(userId, kind)); }
export function resetFeatureConsentForTests() { confirmed.clear(); }
